import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';

interface Player {
    id: string;
    firstName: string;
    lastName: string;
    grade: number;
    gamerTag?: string;
    discordUsername?: string;
    status: string;
    season?: string;
    games: {
        smashSolo?: boolean;
        smashCrews?: boolean;
        smashJVCrews?: boolean;
        madden?: boolean;
        rocketLeague?: boolean;
        marioKart?: boolean;
        fortnite?: boolean;
        broadcasting?: boolean;
    };
    isCaptain?: Record<string, boolean>;
}

const CURRENT_SEASON = 'Spring 2026';

const GAMES = [
    { id: 'smashSolo', name: 'Smash Bros Solo', icon: '🎮', color: 'bg-red-500' },
    { id: 'smashCrews', name: 'Smash Bros Crews', icon: '👥', color: 'bg-red-600' },
    { id: 'smashJVCrews', name: 'Smash Bros JV Crews', icon: '🛡️', color: 'bg-amber-500' },

    { id: 'madden', name: 'Madden NFL', icon: '🏈', color: 'bg-orange-500' },
    { id: 'fortnite', name: 'Fortnite', icon: '🎯', color: 'bg-purple-500' },
    { id: 'broadcasting', name: 'Broadcasting', icon: '📹', color: 'bg-gray-500' },
] as const;

export default function RosterPage() {
    const [players, setPlayers] = useState<Player[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadRoster() {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, 'playerRoster'));
                const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as Player));

                // Filter: Active status AND Current Season
                const activePlayers = data.filter(p =>
                    p.status === 'active' &&
                    (!p.season || p.season === CURRENT_SEASON)
                );

                setPlayers(activePlayers.sort((a, b) => a.lastName.localeCompare(b.lastName)));
            } catch (err) {
                console.error('Error loading roster:', err);
            } finally {
                setLoading(false);
            }
        }
        loadRoster();
    }, []);

    // Helper to get players for a game
    const getPlayersForGame = (gameId: string) => {
        return players.filter(p => p.games?.[gameId as keyof typeof p.games]);
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center min-h-[50vh]">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
                <h1 className="text-3xl md:text-5xl font-display font-bold text-white mb-2 md:mb-4">Team Roster</h1>
                <p className="text-xl text-secondary-400">Peck Pirates Esports • {CURRENT_SEASON}</p>
            </div>

            <div className="space-y-12">
                {GAMES.map(game => {
                    const gamePlayers = getPlayersForGame(game.id);
                    if (gamePlayers.length === 0) return null;

                    // Sort: Captains first, then alphabetical
                    gamePlayers.sort((a, b) => {
                        const aCap = a.isCaptain?.[game.id] || false;
                        const bCap = b.isCaptain?.[game.id] || false;
                        if (aCap !== bCap) return aCap ? -1 : 1;
                        return a.lastName.localeCompare(b.lastName);
                    });

                    return (
                        <div key={game.id} className="scroll-mt-24" id={game.id}>
                            <div className="flex items-center gap-4 mb-4 md:mb-6">
                                <div className={`w-10 h-10 md:w-12 md:h-12 ${game.color} rounded-xl flex items-center justify-center text-xl md:text-2xl shadow-lg`}>
                                    {game.icon}
                                </div>
                                <h2 className="text-2xl md:text-3xl font-display font-bold text-white">{game.name}</h2>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {gamePlayers.map(player => {
                                    const isCaptain = player.isCaptain?.[game.id] || false;

                                    return (
                                        <div
                                            key={player.id}
                                            className={`card p-4 flex items-center gap-4 transition-transform hover:scale-[1.02] ${isCaptain ? 'border-l-4 border-l-yellow-400' : ''}`}
                                        >
                                            <div className="w-12 h-12 bg-secondary-700 rounded-full flex items-center justify-center text-secondary-300 font-bold text-lg">
                                                {player.firstName[0]}{player.lastName[0]}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-bold text-white truncate text-lg">
                                                        {player.firstName} {player.lastName}
                                                    </h3>
                                                    {isCaptain && <span title="Team Captain">👑</span>}
                                                </div>

                                                {player.gamerTag && (
                                                    <div className="text-primary-400 text-sm font-medium truncate">
                                                        "{player.gamerTag}"
                                                    </div>
                                                )}

                                                <div className="text-secondary-400 text-xs mt-1 flex items-center gap-3">
                                                    <span>Grade {player.grade}</span>
                                                    {player.discordUsername && (
                                                        <span className="flex items-center gap-1 text-indigo-400">
                                                            <span className="opacity-70">Discord:</span> @{player.discordUsername}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {players.length === 0 && (
                <div className="text-center py-20 text-secondary-500">
                    <p className="text-xl">Roster not yet finalized for {CURRENT_SEASON}.</p>
                </div>
            )}
        </div>
    );
}
